import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta, organizationSchema, faqSchema } from '../lib/seo';
import { siteConfig } from '../siteConfig';
import { homeFaqs, industries, offers } from '../data/siteData';
import {
  ButtonLink,
  Card,
  Container,
  DemoCallout,
  FAQList,
  FeatureList,
  PageHero,
  PriceCard,
  Section,
  SectionHeading,
  StatGrid,
} from '../components/SiteUi';

const steps = [
  { title: 'We map the missed calls', description: 'We look at how calls, enquiries and bookings arrive now, and where they get lost.' },
  { title: 'We set up the flow', description: 'Website, chatbot, AI receptionist, booking rules, calendar and call forwarding.' },
  { title: 'We test it properly', description: 'After-hours, busy times, emergencies, reschedules, bad lines and requests for a human.' },
  { title: 'We manage it', description: 'You get summaries, reporting and ongoing tuning. You do not have to become a chatbot operator.' },
];

export default function HomePage() {
  const jsonLd = [
    organizationSchema(),
    {
      '@type': 'WebSite',
      '@id': `${siteConfig.url}/#website`,
      url: siteConfig.url,
      name: siteConfig.name,
      publisher: { '@id': `${siteConfig.url}/#organization` },
    },
    faqSchema(homeFaqs),
  ];

  usePageMeta({
    title: 'AI Receptionists, Websites and Custom Software – CloudNext',
    description: 'CloudNext sets up and runs AI receptionists, websites and chatbots for Australian trades and service businesses. Custom software for everyone else.',
    path: '/',
    jsonLd,
  });

  return (
    <>
      <PageHero
        eyebrow="Missed calls are lost money."
        title={<>Never miss another <span className="text-gold-gradient">customer call.</span></>}
        description="CloudNext sets up and manages AI receptionists, websites and chatbots for trades and service businesses. Answer every call, capture every lead, and book more jobs without adding reception staff."
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink to="/contact">Book a call</ButtonLink>
          <ButtonLink to="/pricing" variant="secondary">See pricing</ButtonLink>
          <ButtonLink to="/demo" variant="secondary">Hear the live demo</ButtonLink>
        </div>
        <div className="mt-8 flex flex-wrap gap-2 text-xs text-dust">
          <span className="rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1.5">Based in Hervey Bay</span>
          <span className="rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1.5">Serving Australia-wide</span>
          <span className="rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1.5">Trades first</span>
          <span className="rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1.5">Powered by BulletChat</span>
        </div>
      </PageHero>

      <Section>
        <Container>
          <SectionHeading
            eyebrow="Two ways we help"
            title="Websites + chatbot + AI receptionist bundle, or custom software."
            description="The bundle is for trades and service businesses that want the phone and website handled. Custom software is for businesses that need an app, an MVP or a SaaS product."
          />
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <Card className="flex flex-col">
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">For trades and service businesses</span>
              <h3 className="mt-3 font-display text-2xl text-cream">AI receptionist, website and chatbot, managed.</h3>
              <p className="mt-3 text-sm leading-6 text-dust">
                We set it up, test it and manage it. You keep your number, your calendar and your team. The AI handles the calls and enquiries you cannot get to.
              </p>
              <FeatureList
                className="mt-5"
                items={['AI phone receptionist and after-hours answering', 'Website chatbot that collects enquiries', 'Appointment booking and rescheduling', 'A website built to get enquiries, not just look good']}
              />
              <div className="mt-6 flex flex-wrap gap-3">
                <ButtonLink to="/ai-receptionist">AI receptionist</ButtonLink>
                <ButtonLink to="/websites" variant="secondary">Websites</ButtonLink>
              </div>
            </Card>
            <Card className="flex flex-col">
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">For founders and software teams</span>
              <h3 className="mt-3 font-display text-2xl text-cream">Custom software, MVPs and SaaS.</h3>
              <p className="mt-3 text-sm leading-6 text-dust">
                The existing CloudNext track: custom web and mobile apps, MVPs, SaaS platforms and AI integration. Senior delivery, fixed-fee scoping and direct communication.
              </p>
              <FeatureList
                className="mt-5"
                items={['MVP and SaaS development', 'Custom web and mobile apps', 'AI integration and automation', '20+ years of software experience']}
              />
              <div className="mt-6 flex flex-wrap gap-3">
                <ButtonLink to="/custom-software" variant="secondary">Custom software</ButtonLink>
                <ButtonLink to="/work" variant="ghost">See the work →</ButtonLink>
              </div>
            </Card>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading
            eyebrow="The problem"
            title="The lead usually goes to the business that answers first."
            description="For a trade, a missed call is rarely a missed call. It is a job that went to someone else."
          />
          <StatGrid
            items={[
              { value: 'After hours', label: 'Calls that go to voicemail while the business is closed, driving or on the tools.' },
              { value: "When you're busy", label: 'Calls that arrive while the team is already on another job or with a customer.' },
              { value: 'Slow quotes', label: 'Enquiries that wait hours for a reply, then buy from the next business.' },
            ]}
          />
          <p className="mt-6 max-w-3xl text-sm leading-6 text-ash">
            The numbers depend on the business. That is why the first step is a call-flow review, not a sales pitch.
          </p>
          <div className="mt-6">
            <ButtonLink to="/tools/missed-call-calculator" variant="secondary">Calculate what missed calls could cost</ButtonLink>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading
            eyebrow="How it works"
            title="We set it up. We keep it running."
            description="You do not need to learn a chatbot builder or become a phone-system expert. That is the point of the service."
          />
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {steps.map((step, index) => (
              <Card key={step.title}>
                <span className="font-mono-accent text-xs text-gold-light">0{index + 1}</span>
                <h3 className="mt-3 font-display text-xl text-cream">{step.title}</h3>
                <p className="mt-2 text-sm leading-6 text-dust">{step.description}</p>
              </Card>
            ))}
          </div>
          <div className="mt-10">
            <DemoCallout />
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading
            eyebrow="Trades first"
            title="Built around how trade businesses actually work."
            description="Missed calls, urgent jobs, quote requests, reschedules and after-hours enquiries. Start with your trade."
          />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {industries.map((industry) => (
              <Link key={industry.slug} to={`/industries/${industry.slug}`} className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 transition hover:-translate-y-0.5 hover:border-gold/40">
                <h3 className="font-display text-lg text-cream group-hover:text-gold-light">{industry.name}</h3>
                <p className="mt-2 text-sm leading-6 text-dust">{industry.intro}</p>
                <span className="mt-4 inline-block text-sm text-gold-light">See the {industry.name.toLowerCase()} setup →</span>
              </Link>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading
            eyebrow="Pricing"
            title="Two bundles. No website-only pricing."
            description="Choose Website + Chatbot, or add the AI receptionist. Special setup price is 50% off until 31 December 2026."
          />
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {offers.map((offer) => (
              <PriceCard key={offer.key} offer={offer} featured={offer.key === 'websiteChatbotVoice'} />
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <ButtonLink to="/pricing" variant="secondary">See the full pricing guide</ButtonLink>
            <p className="text-xs leading-5 text-ash">Special pricing ends 31 December 2026. Standard pricing applies from 1 January 2027.</p>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <SectionHeading
                eyebrow="Early clients"
                title="Setup waived for the first five trades businesses."
                description="The AI receptionist is live. The setup fee is waived for the first five trades businesses who sign up before 31 December 2026. Monthly pricing stays as shown above."
              />
              <FeatureList
                className="mt-6"
                items={[
                  'A test run before you commit',
                  'Tested call handling, booking and a plan for when it can’t answer',
                  'A written scope and quote',
                  'A case study only with your permission',
                  'A Google review is never required',
                ]}
              />
              <div className="mt-6 flex flex-wrap gap-3">
                <ButtonLink to="/contact">Ask about the setup waiver</ButtonLink>
                <ButtonLink to="/case-studies" variant="secondary">How case studies work</ButtonLink>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="Questions" title="What business owners usually ask." />
          <div className="mt-8">
            <FAQList items={homeFaqs} />
          </div>
        </Container>
      </Section>

      <Section className="border-t border-white/[0.04]">
        <Container>
          <div className="rounded-[2rem] border border-gold/20 bg-gradient-to-br from-gold/10 via-white/[0.03] to-transparent p-8 md:p-12">
            <SectionHeading
              eyebrow="Next step"
              title="Tell us where the leads are getting lost."
              description="A 15-minute call is enough to work out whether an AI receptionist, a website, a chatbot or a custom build is the right next move."
            />
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <ButtonLink to="/contact">Book a call</ButtonLink>
              <ButtonLink to="/ai-receptionist" variant="secondary">See the AI receptionist</ButtonLink>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
